/**
 * frontend/pages/CreatorDiscovery.jsx
 * SoundDAO Sprint 2 — Creator discovery grid
 *
 * Route: /creators   (no auth required)
 *
 * Features:
 *   - 12-per-page grid of onboarded creator profiles
 *   - Sort by Newest (default) or Top Artists
 *   - Pagination (prev / next / page numbers)
 *   - Each card links to /creator/:creator_id
 *
 * Data: GET /api/creators?page=N&sort=fans|newest
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './CreatorDiscovery.css';
import '../styles/archive.css';
import BrandMark from '../components/BrandMark';
import DigHero from '../components/DigHero';
import { fetchCreatorsPage } from '../lib/creatorsList';


// ── Sprint 5C — Stage badge text ──────────────────────────────────────────────
function getStageBadgeText(stage) {
  const map = {
    'Flair':    '🎭 Flair',
    'Burning':  '🔥 Burning',
    'Wildfire': '🌪️ Wildfire',
    'Phoenix':  '🔥 Phoenix',
  };
  return map[stage] || stage;
}

// ── CreatorCard ────────────────────────────────────────────────────────────────
function CreatorCard({ creator }) {
  const frenLive = creator.fren_status === 'launched';
  return (
    <Link to={`/creator/${creator.user_id}`} className="cd-card">
      <div className="cd-card-avatar-wrap">
        {creator.avatar_url ? (
          <img src={creator.avatar_url} alt={creator.artist_name} className="cd-card-avatar" />
        ) : (
          <div className="cd-card-avatar-fallback">
            {(creator.artist_name || '?')[0].toUpperCase()}
          </div>
        )}
        {frenLive && <div className="cd-card-fren-badge">🚀</div>}
        {creator.video_url && <div className="cd-card-video-badge">▶</div>}
      </div>

      <div className="cd-card-info">
        <h3 className="cd-card-name">{creator.artist_name}</h3>
        {creator.genre && <span className="cd-card-genre">{creator.genre}</span>}
        {creator.stage && creator.stage !== 'Raw' && (
          <span className={`cd-stage-badge cd-stage-badge--${creator.stage.toLowerCase()}`}>
            {getStageBadgeText(creator.stage)}
          </span>
        )}
        <div className="cd-card-stats">
          <span>{(creator.total_fans || 0).toLocaleString()} fans</span>
        </div>
      </div>

      {frenLive && (
        <div className="cd-card-live-badge">FReN Live</div>
      )}
    </Link>
  );
}

// ── EmptyState ─────────────────────────────────────────────────────────────────
function EmptyState() {
  return (
    <div className="cd-empty">
      <div className="cd-empty-icon">🎵</div>
      <h2>No creators yet</h2>
      <p>Be the first to set up your creator profile.</p>
    </div>
  );
}

// ── CreatorDiscovery ───────────────────────────────────────────────────────────
// controlsStart: optional node shown at the start of the sort row (DiscoverTab puts its
// Browse/Invite toggle there). onTotal: optional callback with the creator count.
export default function CreatorDiscovery({ session, showDigEntry = false, controlsStart = null, onTotal }) {
  const [creators, setCreators]  = useState([]);
  const [page,     setPage]      = useState(1);
  const [pages,    setPages]     = useState(1);
  const [total,    setTotal]     = useState(0);
  const [sort,     setSort]      = useState('newest'); // 'newest' | 'fans'
  const [loading,  setLoading]   = useState(true);
  const [error,    setError]     = useState('');

  const loadPage = useCallback(async (p, s) => {
    setLoading(true);
    setError('');
    try {
      // Public list: plain fetch through lib/creatorsList (cached, prefetched at app start).
      const data = await fetchCreatorsPage(p, s === 'fans' ? 'fans' : 'newest');
      setCreators(data.creators || []);
      setPage(data.page || 1);
      setPages(data.pages || 1);
      setTotal(data.total || 0);
      if (onTotal) onTotal(data.total || 0);
    } catch (err) {
      setError('Could not load creators. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [onTotal]);

  useEffect(() => {
    loadPage(1, sort);
  }, [sort, loadPage]);

  const handleSort = (newSort) => {
    if (newSort === sort) return;
    setSort(newSort);
    setPage(1);
  };

  const handlePage = (p) => {
    if (p < 1 || p > pages || p === page) return;
    loadPage(p, sort);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="cd-screen">
      {/* Nav */}
      <nav className="cd-nav">
        <Link to="/" className="cd-nav-logo"><BrandMark /> SoundDAO</Link>
        {session ? (
          <Link to={`/dashboard/player`} className="cd-nav-link">Dashboard</Link>
        ) : (
          <Link to="/" className="cd-nav-link">Sign In</Link>
        )}
      </nav>

      {/* Sprint 6A — Dig the Archive entry for creators and collectors (players get it on the Discover tab) */}
      {/* One 560px column: hero, header, sort row, grid and pagination share the same edges. */}
      <div className="cd-col">
      {showDigEntry && (
        <div className="cd-dig-hero-wrap">
          <DigHero />
        </div>
      )}

      {/* Page header */}
      <div className="cd-page-header">
        <h1 className="cd-page-title">Discover Artists</h1>
        <p className="cd-page-sub">
          {loading ? '' : `${total.toLocaleString()} creator${total !== 1 ? 's' : ''} on SoundDAO`}
        </p>
      </div>

      {/* Sort controls */}
      <div className={`cd-controls${controlsStart ? ' cd-controls--with-start' : ''}`}>
        {controlsStart}
        <div className="cd-sort-tabs">
          <button
            className={`cd-sort-tab ${sort === 'newest' ? 'cd-sort-tab--active' : ''}`}
            onClick={() => handleSort('newest')}
          >
            Newest
          </button>
          <button
            className={`cd-sort-tab ${sort === 'fans' ? 'cd-sort-tab--active' : ''}`}
            onClick={() => handleSort('fans')}
          >
            Top Artists
          </button>
        </div>
      </div>

      {/* Grid */}
      <div className="cd-main">
        {loading ? (
          <div className="cd-grid cd-grid--loading" aria-busy="true" aria-label="Loading artists">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="cd-card cd-card--skel" aria-hidden="true">
                <div className="cd-skel cd-skel--avatar" />
                <div className="cd-skel cd-skel--line" />
                <div className="cd-skel cd-skel--line cd-skel--short" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="cd-error">
            <p>{error}</p>
            <button className="cd-retry-btn" onClick={() => loadPage(page, sort)}>Try again</button>
          </div>
        ) : creators.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="cd-grid">
            {creators.map(c => (
              <CreatorCard key={c.user_id} creator={c} />
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {!loading && pages > 1 && (
        <div className="cd-pagination">
          <button
            className="cd-page-btn"
            onClick={() => handlePage(page - 1)}
            disabled={page === 1}
          >
            ← Prev
          </button>

          <div className="cd-page-numbers">
            {Array.from({ length: pages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === pages || Math.abs(p - page) <= 2)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && arr[idx - 1] !== p - 1) acc.push('…');
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === '…' ? (
                  <span key={`ellipsis-${idx}`} className="cd-page-ellipsis">…</span>
                ) : (
                  <button
                    key={item}
                    className={`cd-page-number ${item === page ? 'cd-page-number--active' : ''}`}
                    onClick={() => handlePage(item)}
                  >
                    {item}
                  </button>
                )
              )
            }
          </div>

          <button
            className="cd-page-btn"
            onClick={() => handlePage(page + 1)}
            disabled={page === pages}
          >
            Next →
          </button>
        </div>
      )}
      </div>
    </div>
  );
}
